import { SchedulingGuideLink } from "@/components/site/SchedulingGuideLink";
import { RecentApprovalsTicker } from "@/components/site/RecentApprovalsTicker";
import { RefusalNotice } from "@/components/site/RefusalNotice";
import { getSocialMeta } from "@/lib/social-meta.functions";
import { SOCIAL_META_DEFAULTS } from "@/lib/social-meta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
import { FirstSessionPromise } from "@/components/site/FirstSessionPromise";
import {
  ArrowRight,
  CheckCircle2,
  BookOpen,
  Sparkles,
  Users,
  FileCheck2,
  MessageCircle,
  Brain,
  Zap,
  ShieldCheck,
  ClipboardList,
  CalendarCheck,
  Target,
  Trophy,
  XCircle,
  Bot,
  UserCheck,
} from "lucide-react";

import blob from "@/assets/blob-accent.webp";
import { useSiteContent } from "@/hooks/useSiteContent";
import { TestimonialsPreview } from "@/components/site/TestimonialsPreview";

const TestimonialsSection = lazy(() =>
  import("@/components/ui/testimonials-section").then((m) => ({ default: m.TestimonialsSection })),
);
const FeaturedAchievements = lazy(() =>
  import("@/components/site/FeaturedAchievements").then((m) => ({ default: m.FeaturedAchievements })),
);
const GroupSignupDialog = lazy(() =>
  import("@/components/site/GroupSignupDialog").then((m) => ({ default: m.GroupSignupDialog })),
);

export const Route = createFileRoute("/")({
  loader: async () => {
    try {
      return { social: await getSocialMeta() };
    } catch {
      return { social: SOCIAL_META_DEFAULTS };
    }
  },
  head: ({ loaderData }) => {
    const social = loaderData?.social ?? SOCIAL_META_DEFAULTS;
    return ({
    meta: [
      { title: "WeLivdIt · 1,500+ US Visa Approvals | 1-on-1 Visa Coaching" },
      {
        name: "description",
        content:
          "1,500+ students approved since 2021. Master your DS-160 narrative & practice real consular drills before your interview.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://welivdit.com" },
      { property: "og:title", content: social.title },
      { property: "og:description", content: social.description },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:alt", content: "WeLivdIt - 1,500+ US visa approvals, 4.7 stars on Trustpilot" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: social.title },
      { name: "twitter:description", content: social.description },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://welivdit.com/" },
      { rel: "preload", as: "image", type: "image/webp", href: blob, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "WeLivdIt",
          legalName: "WeLivdIt Private Limited",
          url: "https://welivdit.com",
          logo: "https://welivdit.com/favicon.png",
          description: "The WeLivdIt Method helps students clear US visa interviews with 1-on-1 mock sessions, DS-160 review, and 214(b) refusal recovery. Built on 1,500+ approvals.",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Hyderabad",
            addressRegion: "Telangana",
            addressCountry: "IN",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.7",
            reviewCount: "264",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "WeLivdIt",
          url: "https://welivdit.com",
        }),
      },
    ],
  });
  },
  component: HomePage,
});


function HomePage() {
  const { t } = useSiteContent();
  const [signupOpen, setSignupOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<"all" | "fresher" | "refusal" | "practice">("all");

  return (
    <>
      {signupOpen && (
        <Suspense fallback={null}>
          <GroupSignupDialog open={signupOpen} onOpenChange={setSignupOpen} />
        </Suspense>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-hero">
        <img
          src={blob}
          alt=""
          aria-hidden
          width={500}
          height={500}
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className="pointer-events-none absolute -top-32 -right-32 w-[500px] opacity-70 blur-2xl"
        />
        <div className="relative mx-auto max-w-6xl px-5 pt-2 pb-8 md:pt-3 md:pb-10">
          <div className="max-w-3xl">
            {/* Live Activity Pill + Social Proof */}
            <div className="inline-flex flex-wrap items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-border text-xs shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-foreground/40" aria-hidden="true">
                |
              </span>
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="text-foreground/90 font-medium">{t("hero.badge_approved")}</span>
              <span className="text-foreground/40" aria-hidden="true">
                |
              </span>
              <span className="text-foreground/90 font-medium">{t("hero.badge_rating")}</span>
            </div>

            <h1 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-balance leading-[1.05]">
              {t("hero.headline")}
              <br />
              <em className="not-italic text-primary">{t("hero.headline_accent")}</em>
              <span className="block mt-3 text-lg sm:text-xl md:text-2xl font-medium text-foreground/70">
                {t("hero.subhead")}
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-foreground/70 max-w-xl leading-relaxed">
              {t("hero.paragraph")}
            </p>
            <p className="mt-3 text-sm text-foreground/60 max-w-xl leading-relaxed">{t("hero.proof_note")}</p>

            <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
              <a
                href="#programs"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition min-w-[260px] ring-2 ring-primary/30"
              >
                {t("hero.cta_primary")} <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/find-your-plan"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border-2 border-primary/40 bg-white/70 text-primary font-semibold hover:bg-primary/10 transition min-w-[260px]"
              >
                {t("hero.cta_secondary")} <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={() => setSignupOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-border bg-white/70 text-foreground font-semibold hover:bg-white transition min-w-[260px]"
              >
                {t("hero.cta_tertiary")} <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {/* Supported Visa Categories Badge Bar */}
            <div className="mt-8 flex flex-wrap items-center gap-2 text-xs font-medium text-foreground/80">
              <span className="text-foreground/50 mr-1">We prepare candidates for:</span>
              <span className="rounded-full bg-white/80 px-3 py-1 border border-border shadow-xs">F-1 Student</span>
              <span className="rounded-full bg-white/80 px-3 py-1 border border-border shadow-xs">J-1 Exchange</span>
              <span className="rounded-full bg-white/80 px-3 py-1 border border-border shadow-xs">M-1 / M-2</span>
              <span className="rounded-full bg-white/80 px-3 py-1 border border-border shadow-xs">
                B-1 / B-2 Visitor
              </span>
              <span className="rounded-full bg-white/80 px-3 py-1 border border-border shadow-xs">H-1B Work</span>
              <span className="rounded-full bg-primary/10 text-primary px-3 py-1 border border-primary/30 font-semibold">
                214(b) Refusals
              </span>
            </div>

            <div className="mt-5">
              <FirstSessionPromise variant="inline" />
            </div>
          </div>
        </div>
        <div className="relative border-t border-border/60 bg-background/60 backdrop-blur-sm">
          <RecentApprovalsTicker className="!py-4" />
        </div>
      </section>

      {/* TESTIMONIAL CAROUSEL */}
      <section className="relative overflow-hidden bg-marquee-banner">
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 lg:px-8 py-4 md:py-6">
          <TestimonialsPreview />
          <SchedulingGuideLink className="mt-8" />
        </div>
      </section>

      {/* SERVICES MATRIX */}
      <section className="mx-auto max-w-6xl px-5 pt-28 pb-14 md:pt-32 md:pb-16">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-[11px] font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="h-3 w-3" /> The WeLivdIt Method
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
              We catch the answer that gets people refused,
              <em className="not-italic text-primary"> before you say it.</em>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 hidden md:block">
            <div className="h-px w-full bg-border" />
            <p className="mt-4 text-sm text-foreground/60 leading-relaxed">
              The WeLivdIt Method: six layers of prep, refined across 1,500+ approvals.
            </p>
          </div>
        </div>

        {/* Step bar */}
        <div className="mt-8 rounded-2xl border border-border bg-accent/30 p-4 md:p-5">
          <ol className="flex flex-wrap items-center gap-x-1 gap-y-3 text-xs md:text-sm">
            {[
              { n: "01", label: "Diagnose", sub: "Mock Interviews" },
              { n: "02", label: "Rebuild", sub: "DS-160 Review" },
              { n: "03", label: "Drill", sub: "Visa Officer Logic" },
              { n: "04", label: "Stress-Test", sub: "Spontaneous Training" },
              { n: "05", label: "Community", sub: "Success Community" },
              { n: "06", label: "Final Mile", sub: "Last-Mile Strategy" },
            ].map((s, i, arr) => (
              <li key={s.n} className="flex items-center gap-1">
                <div className="flex flex-col">
                  <span className="font-semibold text-primary">
                    {s.n} · {s.label}
                  </span>
                  <span className="text-[10px] md:text-xs text-foreground/55">{s.sub}</span>
                </div>
                {i < arr.length - 1 && <span className="text-foreground/30 mx-2">→</span>}
              </li>
            ))}
          </ol>
        </div>

        {/* Featured leader cards */}
        <div className="mt-10 grid md:grid-cols-12 gap-5">
          <div className="md:col-span-7 group relative rounded-[2rem] bg-card border border-border p-8 md:p-10 hover:shadow-soft hover:-translate-y-1 transition-all overflow-hidden">
            <div
              className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-accent opacity-40 blur-2xl"
              aria-hidden="true"
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Core service
              </div>
              <div className="mt-4 h-14 w-14 rounded-2xl bg-accent grid place-items-center text-primary">
                <MessageCircle className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-2xl md:text-3xl font-semibold">Mock Interviews</h3>
              <p className="mt-3 text-foreground/75 leading-relaxed max-w-lg">
                1-on-1 and group mocks with real visa-officer style questioning. Body language, structure, edge cases —
                everything.
              </p>
            </div>
          </div>

          <div className="md:col-span-5 group relative rounded-3xl bg-accent/40 border border-border p-7 hover:-translate-y-1 transition-all">
            <div className="h-12 w-12 rounded-xl bg-card border border-border grid place-items-center text-primary">
              <FileCheck2 className="h-5 w-5" />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold">DS-160 Review</h3>
            <p className="mt-2 text-sm text-foreground/70 leading-relaxed">
              One wrong line on your DS-160 ends the interview. We read every line with you.
            </p>
          </div>
        </div>

        {/* Remaining services */}
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
          {[
            {
              icon: Brain,
              title: "Visa Officer Logic",
              body: "Decode how officers think. Build sharp, consistent answers that hold up.",
            },
            {
              icon: Zap,
              title: "Spontaneous Training",
              body: "Curveball drills - answer the unexpected calmly, no scripts.",
            },
            {
              icon: Users,
              title: "Success Community",
              body: "Live sessions and a circle of people walking the same path.",
            },
            {
              icon: ShieldCheck,
              title: "Last-Mile Strategy",
              body: "Final-week prep - opening line, body language, 24h checklist.",
            },
          ].map((s) => (
            <div key={s.title} className="bg-card p-6 hover:bg-accent/30 transition-colors">
              <s.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-4 font-display text-base font-semibold">{s.title}</h3>
              <p className="mt-1.5 text-xs text-foreground/65 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE: WELIVDIT vs AI vs GENERIC AGENCIES */}
      <section className="mx-auto max-w-5xl px-5 py-16 md:py-20">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-10 shadow-sm">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-[11px] font-semibold uppercase tracking-wider text-primary">
              <Brain className="h-3.5 w-3.5" /> The WeLivdIt Advantage
            </div>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
              Why AI and traditional consultancies fail at the consulate.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              AI listens to your words. Consultancies give generic scripts. WeLivdIt trains your real-time judgment
              under officer pressure.
            </p>
          </div>

          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-left text-xs md:text-sm border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="py-3 px-4 font-semibold">Interview Factor</th>
                  <th className="py-3 px-4 font-semibold text-primary bg-primary/10 rounded-t-xl">
                    <span className="flex items-center gap-1.5 font-bold text-foreground">
                      <UserCheck className="h-4 w-4 text-primary" /> WeLivdIt Specialists
                    </span>
                  </th>
                  <th className="py-3 px-4 font-semibold">
                    <span className="flex items-center gap-1.5">
                      <Bot className="h-4 w-4 text-muted-foreground" /> Generic AI Apps
                    </span>
                  </th>
                  <th className="py-3 px-4 font-semibold">Traditional Consultancies</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">Officer Interruption &amp; Stress Test</td>
                  <td className="py-3.5 px-4 bg-primary/5 font-semibold text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-4 w-4 inline mr-1" /> Sharp, realistic cut-offs &amp; pivot drills
                  </td>
                  <td className="py-3.5 px-4 text-muted-foreground">
                    <XCircle className="h-4 w-4 inline mr-1 text-red-400" /> Waits for full paragraph
                  </td>
                  <td className="py-3.5 px-4 text-muted-foreground">Monotone question lists</td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">DS-160 Line-by-Line Audit</td>
                  <td className="py-3.5 px-4 bg-primary/5 font-semibold text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-4 w-4 inline mr-1" /> Expert line-by-line verification
                  </td>
                  <td className="py-3.5 px-4 text-muted-foreground">
                    <XCircle className="h-4 w-4 inline mr-1 text-red-400" /> Zero DS-160 inspection
                  </td>
                  <td className="py-3.5 px-4 text-muted-foreground">Standard template check</td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">214(b) Refusal Recovery Strategy</td>
                  <td className="py-3.5 px-4 bg-primary/5 font-semibold text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-4 w-4 inline mr-1" /> Full interview transcript teardown
                  </td>
                  <td className="py-3.5 px-4 text-muted-foreground">
                    <XCircle className="h-4 w-4 inline mr-1 text-red-400" /> Surface-level advice
                  </td>
                  <td className="py-3.5 px-4 text-muted-foreground">Blames bad officer luck</td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">Tailored Strategy &amp; Pitch</td>
                  <td className="py-3.5 px-4 bg-primary/5 font-bold text-primary">
                    <CheckCircle2 className="h-4 w-4 inline mr-1 text-primary" /> 100% personalized background mapping
                  </td>
                  <td className="py-3.5 px-4 text-muted-foreground">Generic ChatGPT responses</td>
                  <td className="py-3.5 px-4 text-muted-foreground">One-size-fits-all scripts</td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">Pricing Structure</td>
                  <td className="py-3.5 px-4 bg-primary/5 font-bold text-primary">Priced in ₹ (Rupees)</td>
                  <td className="py-3.5 px-4 text-muted-foreground">US Dollar Subscriptions</td>
                  <td className="py-3.5 px-4 text-muted-foreground">High Agency Commissions</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FOUNDER PLEDGE & EMOTIONAL CONNECTION */}
      <section className="mx-auto max-w-5xl px-5 py-14 md:py-18">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-b from-card to-accent/30 border border-border p-8 md:p-12 shadow-sm text-center md:text-left">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8 space-y-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider">
                ❤️ Built By People Who Stood In Your Shoes
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                We know what it feels like to stand in that line.
              </h2>
              <p className="text-sm sm:text-base text-foreground/80 leading-relaxed">
                The nervous energy in your stomach, holding your DS-160 folder tightly, and watching passport slips get
                handed out at the counter. Consular officers do not reject applicants because they lack ambition. They
                reject people who sound like they are reciting a memorized script.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We created WeLivdIt to change that. We do not sell generic templates or robotic feedback. We train your
                real-time confidence, fix your DS-160 red flags, and stay in your corner until you walk out approved.
              </p>
            </div>

            <div className="md:col-span-4 flex flex-col items-center justify-center p-6 rounded-2xl bg-card border border-border/80 text-center shadow-xs">
              <div className="text-3xl font-display font-bold text-primary">{t("story.stat_value")}</div>
              <p className="text-xs font-medium text-foreground mt-0.5">{t("story.stat_label")}</p>
              <div className="my-3 h-px w-12 bg-border" />
              <p className="text-xs text-muted-foreground italic">
                &quot;{t("story.quote")}&quot;
              </p>
              <span className="mt-3 text-xs font-bold text-foreground">Welivdit Team</span>
            </div>
          </div>
        </div>
      </section>

      {/* SPECIALIZED PROGRAMS WITH INTERACTIVE FILTER */}
      <section id="programs" className="mx-auto max-w-6xl px-5 pt-10 pb-20 md:pt-12 md:pb-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-primary">Specialized Programs</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
              Pick the path that fits you.
            </h2>
            <p className="mt-2 text-foreground/70 text-sm">
              Three programs — from quick practice to full prep for first-timers and re-attempts.
            </p>
          </div>

          {/* Interactive Filter Pills */}
          <div className="flex flex-wrap gap-2">
            {[
              { id: "all", label: "All Programs" },
              { id: "fresher", label: "First-Time Freshers" },
              { id: "refusal", label: "214(b) Refusals" },
              { id: "practice", label: "Quick Mocks" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setSelectedFilter(tab.id as any)}
                className={`px-3.5 py-1.5 text-xs rounded-full border transition-all font-medium ${
                  selectedFilter === tab.id
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-card text-muted-foreground border-border hover:border-primary/50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <RefusalNotice className="mt-8" />
        <div className="mt-6 grid md:grid-cols-3 gap-5 md:items-center">
          {/* Basic Support */}
          <Link
            to="/basic-support"
            className={`group relative overflow-hidden rounded-3xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-card p-7 transition-all duration-300 ${
              selectedFilter === "practice" || selectedFilter === "all"
                ? "opacity-100 scale-100"
                : "opacity-40 scale-95"
            } hover:-translate-y-2 hover:shadow-xl hover:border-cyan-300`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-100 border border-cyan-200 text-cyan-900 text-[11px] font-semibold uppercase tracking-wider">
              <Zap className="h-3 w-3" /> Light practice
            </div>
            <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight">Basic Support</h3>
            <p className="mt-1 text-sm text-foreground/80">If confident, need some mock practise</p>
            <p className="mt-4 text-foreground/75 leading-relaxed text-sm">
              Two focused mocks with real-time feedback. Perfect if your DS-160 is clean and you just need structured
              rehearsal.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-foreground/80">
              {["2 one-on-one mock interviews", "Documentation assistance", "Line-by-line feedback"].map((i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  {i}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <span className="font-display text-2xl font-semibold">₹3,000</span>
                <span className="inline-flex items-center gap-1 text-primary font-medium text-sm group-hover:gap-2 transition-all">
                  Explore Basic Support <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </Link>

          {/* Full Support */}
          <Link
            to="/full-support"
            className={`group relative overflow-visible rounded-[2.25rem] border-2 border-primary/60 ring-4 ring-primary/10 bg-gradient-to-br from-sky-50 via-white to-indigo-50 p-8 shadow-2xl shadow-primary/20 transition-all duration-300 ${
              selectedFilter === "fresher" || selectedFilter === "all"
                ? "opacity-100 md:scale-[1.06]"
                : "opacity-40 scale-95"
            } hover:-translate-y-2 hover:shadow-primary/30`}
          >
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-cta text-primary-foreground text-[10px] font-bold uppercase tracking-widest shadow-glow whitespace-nowrap z-10">
              <Sparkles className="h-3 w-3" /> Most Popular
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 border border-orange-200 text-orange-900 text-[11px] font-semibold uppercase tracking-wider">
              <Sparkles className="h-3 w-3" /> For freshers
            </div>
            <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight">Full Support</h3>
            <p className="mt-1 text-sm text-foreground/80">First-time US visa applicants</p>
            <p className="mt-4 text-foreground/75 leading-relaxed text-sm">
              End-to-end prep — 4 mocks, full DS-160 review, profile analysis, and an officer-logic strategy session.
              Walk in calm, leave with the visa.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-foreground/80">
              {["4 one-on-one mock interviews", "Full DS-160 form guidance", "Profile analysis + strategy session"].map(
                (i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                    {i}
                  </li>
                ),
              )}
            </ul>
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <span className="font-display text-2xl font-semibold">₹4,500</span>
                <span className="inline-flex items-center gap-1 text-primary font-medium text-sm group-hover:gap-2 transition-all">
                  Explore Full Support <ArrowRight className="h-4 w-4" />
                </span>
              </div>
              <p className="mt-2 text-xs text-center text-muted-foreground italic">
                🎯 Covers your entire profile strategy, form logic, and up to 4 progressive mocks.
              </p>
            </div>
          </Link>

          {/* Refusal Support */}
          <Link
            to="/refusal-support"
            className={`group relative overflow-hidden rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-card p-7 transition-all duration-300 ${
              selectedFilter === "refusal" || selectedFilter === "all" ? "opacity-100 scale-100" : "opacity-40 scale-95"
            } hover:-translate-y-2 hover:shadow-xl hover:border-amber-300`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 border border-amber-200 text-amber-900 text-[11px] font-semibold uppercase tracking-wider">
              <ShieldCheck className="h-3 w-3" /> For refusals
            </div>
            <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight">Refusal Support</h3>
            <p className="mt-1 text-sm text-foreground/80">After a 214(b) refusal</p>
            <p className="mt-4 text-foreground/75 leading-relaxed text-sm">
              Got a 214(b) refusal slip? We audit your previous interview transcript, rebuild your DS-160 narrative, and
              run 5 hard stress-test mocks until your story holds up under pressure.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-foreground/80">
              {[
                "Refusal transcript & root cause audit",
                "DS-160 red-flag cleanup & re-attempt narrative",
                "5 escalating stress-test mock interviews",
              ].map((i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  {i}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <span className="font-display text-2xl font-semibold">₹7,000</span>
                <span className="inline-flex items-center gap-1 text-primary font-medium text-sm group-hover:gap-2 transition-all">
                  Explore Refusal Support <ArrowRight className="h-4 w-4" />
                </span>
              </div>
              <p className="mt-2 text-xs text-center text-muted-foreground italic">
                🛡️ 100% confidential profile teardown. We fix the exact reason the officer stamped 214(b).
              </p>
            </div>
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-accent/20 border-y border-border">
        <div className="mx-auto max-w-6xl px-5 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-3">
              <div className="h-1 w-12 bg-primary rounded-full" aria-hidden="true" />
              <p className="mt-4 text-sm font-medium text-primary uppercase tracking-wider">How it works</p>
            </div>
            <div className="md:col-span-9">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
                From enrollment to visa stamp — your journey with us.
              </h2>
              <p className="mt-3 text-foreground/70 max-w-xl">
                A clear, friction-free flow once you join. No guesswork, no surprises.
              </p>
            </div>
          </div>

          <div className="hidden lg:block mt-14 relative">
            <div className="timeline-line-h" />
            <div className="grid grid-cols-4 gap-6">
              {[
                {
                  icon: ClipboardList,
                  step: "Step 1",
                  title: "Share your details",
                  body: "Send us your DS-160 and profile info. We study your case end-to-end.",
                },
                {
                  icon: CalendarCheck,
                  step: "Step 2",
                  title: "Book your slots",
                  body: "Lock in mock interview slots that fit your interview date and timezone.",
                },
                {
                  icon: Target,
                  step: "Step 3",
                  title: "Get grilled in tough mocks",
                  body: "Visa-officer style drills. We push until your weakest answer becomes your strongest.",
                },
                {
                  icon: Trophy,
                  step: "Step 4",
                  title: "Final strategy tweak",
                  body: "Last-mile prep before the consulate - opening line, body language, docs checklist.",
                },
              ].map((s, idx) => (
                <div key={s.title} className="flex flex-col items-center text-center">
                  <div className="relative z-10 h-14 w-14 rounded-full bg-gradient-cta text-primary-foreground grid place-items-center text-xl font-bold font-display shadow-glow ring-4 ring-background">
                    {idx + 1}
                  </div>
                  <div className="mt-5 h-12 w-12 rounded-2xl bg-accent grid place-items-center text-primary">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-wider text-primary">{s.step}</p>
                  <h3 className="mt-1 font-display text-lg font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm text-foreground/70 leading-relaxed max-w-[16rem]">{s.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:hidden mt-12 relative">
            <div className="timeline-line-v" />
            <div className="space-y-10">
              {[
                {
                  icon: ClipboardList,
                  step: "Step 1",
                  title: "Share your details",
                  body: "Send us your DS-160 and profile info. We study your case end-to-end.",
                },
                {
                  icon: CalendarCheck,
                  step: "Step 2",
                  title: "Book your slots",
                  body: "Lock in mock interview slots that fit your interview date and timezone.",
                },
                {
                  icon: Target,
                  step: "Step 3",
                  title: "Get grilled in tough mocks",
                  body: "Visa-officer style drills. We push until your weakest answer becomes your strongest.",
                },
                {
                  icon: Trophy,
                  step: "Step 4",
                  title: "Final strategy tweak",
                  body: "Last-mile prep before the consulate - opening line, body language, docs checklist.",
                },
              ].map((s, idx) => (
                <div key={s.title} className="relative flex gap-6">
                  <div className="relative z-10 shrink-0">
                    <div className="h-14 w-14 rounded-full bg-gradient-cta text-primary-foreground grid place-items-center text-xl font-bold font-display shadow-glow ring-4 ring-background">
                      {idx + 1}
                    </div>
                  </div>
                  <div className="pt-1">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-accent grid place-items-center text-primary">
                        <s.icon className="h-5 w-5" />
                      </div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">{s.step}</p>
                    </div>
                    <h3 className="mt-2 font-display text-lg font-semibold">{s.title}</h3>
                    <p className="mt-1 text-sm text-foreground/70 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED ACHIEVEMENTS */}
      <Suspense fallback={null}>
        <FeaturedAchievements />
      </Suspense>

      {/* TESTIMONIALS */}
      <Suspense fallback={null}>
        <TestimonialsSection />
      </Suspense>

      {/* KNOWLEDGE HUB */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-20">
        <div className="rounded-[2rem] border-2 border-primary/30 bg-primary/[0.05] p-7 sm:p-10 md:p-12">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
            <BookOpen className="h-3 w-3" /> Free knowledge hub
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-balance">
            F-1 Visa Knowledge Hub &amp; Checklist
          </h2>
          <p className="mt-4 max-w-2xl text-foreground/70 leading-relaxed">
            Everything we learned from 1,500+ approvals: SEVIS and DS-160 timelines, funding logic, interview
            psychology, and 214(b) recovery strategies. Plus a live Q&amp;A where our Specialists answer your doubt.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-3">
            {[
              "SEVIS & DS-160 timing rules",
              "Funding logic officers accept",
              "214(b) recovery playbook",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-foreground/80">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {t}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/resources/f1-visa-guide"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-cta px-7 py-3.5 font-medium text-primary-foreground shadow-glow transition hover:opacity-90"
            >
              Open the Knowledge Hub <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-7 py-3.5 font-medium transition hover:border-primary/60 hover:text-primary"
            >
              Browse all guides
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}

      <section className="mx-auto max-w-6xl px-5 py-16 md:py-24">
        <div className="rounded-[2rem] bg-gradient-hero border border-border p-8 sm:p-10 md:p-16 text-center relative overflow-hidden">
          <img src={blob} alt="" aria-hidden className="absolute -bottom-40 -left-32 w-[500px] opacity-50 blur-3xl" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-balance">
              Most people fail in the first 30 seconds. You won&apos;t, because we already heard your worst questions.
            </h2>
            <p className="mt-4 text-foreground/70 max-w-xl mx-auto">
              Pick the program that fits your stage and start prepping today.
            </p>
            <Link
              to="/pricing"
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              See our plans <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
